/* eslint-disable max-len */
/* eslint-disable no-nested-ternary */
import React, {
  useState, useCallback, useEffect, useLayoutEffect, useRef,
} from 'react';
import { createPortal } from 'react-dom';
import Shimmer from 'react-shimmer-effect';
import { useDispatch, useSelector } from 'react-redux';
import IcomoonReact from 'icomoon-react';
import styled from '@emotion/styled';
import moment from 'moment-timezone';
import PropTypes from 'prop-types';
import { Tooltip as ReactTooltip } from 'react-tooltip';

import iconSet from '../../../../shared/images/teambuildr-selection.json';
import StatusBadgeScroller from './StatusBadgeScroller';
import hexToRgba from '../../../../shared/utils/hexToRgba';
import { SUPERSET_DEFAULTS } from '../../../../shared/constants';
import {
  saveCalendarExercise,
  saveNonDateBasedWorkoutExercise,
  updateReduxCalendarItem,
  updateReduxProgramItem,
  focusNextPreSaveItem,
  focusNextPreSaveProgramItem,
  setPreSaveTableLocalState,
} from '../../ducks/calendarActions';
import SwapPropDropdownCircuit from './SwapPropDropdownCircuit';
import capitalizeWords from '../../../../shared/utils/capitalizeWords';
import TBportalTooltip from '../../../../shared/components/TBportalTooltip';
import Text from '../../../../shared/components/Text/Text';
import generateUniqueId from '../../../../shared/utils/generateUniqueId';

const FormWrapper = styled('form')`
  display: flex;
  flex-direction: column;
  width: 100%;
`;

const RowValue = styled('input')`
  display: flex;
  padding: 16px;
  justify-content: center;
  align-items: center;
  font-size: 16px;
  font-weight: 400;
  font-family: 'Nunito Sans';
  width: 100%;
  height: 32px;
  background: ${(props) => props.background};
  border: ${(props) => (!props.isError ? '1px solid #EEEEEE' : '1px solid red')};
  text-align: center;
  border-radius: 4px;
  color: ${(props) => (props.textColor ? props.textColor : '#444444')};

  ::placeholder {
    color: ${(props) => (props.textColor ? props.textColor : '#444444')};
  }
`;

const RowValueContainer = styled('div')`
  flex: 1;
  min-width: 180px;
  display: flex;
  justify-content: center;
  align-items: center;
  position: relative;
  border-radius: ${(props) => (props.shimmer ? '4px' : '0px')};
`;

const InputWithErrorWrapper = styled('div')`
  position: relative;
  width: 100%;
`;

const InputError = styled('div')`
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  margin-top: -2px;
  font-size: 11px;
  font-family: 'Nunito Sans';
  color: red;
  white-space: nowrap;
  text-overflow: ellipsis;
  overflow: hidden;
  cursor: default;
`;

const ShimmerRowValue = styled('div')`
  display: flex;
  padding: 16px;
  justify-content: center;
  align-items: center;
  font-size: 16px;
  font-weight: 400;
  font-family: 'Nunito Sans';
  width: 100%;
  height: 32px;
  text-align: center;
  border-radius: 6px;
  color: transparent;
  margin-left: 5px;
  margin-right: 5px;
`;

const NameAndDropdown = styled('div')`
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 3px;
  position: relative;
`;

const InfoIconWrapper = styled('div')`
  position: absolute;
  left: -20px;
  display: flex;
  align-items: center;
`;

const TooltipDataContainer = styled('div')`
  display: flex;
  flex-direction: column;
  justify-content: center;
  padding-top: 10px;
  padding-bottom: 10px;
  padding-right: 16px;
  padding-left: 16px;
  gap: 10px;
  border-radius: 4px;
  max-width: 250px;
`;

/** Transposed-layout + superset-picker styled components. */
const SetsCountHeader = styled('div')`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 12px;
  padding: 20px 0 22px;
`;

const SupersetWrapper = styled('div')`
  position: relative;
  display: inline-flex;
  align-items: center;
  gap: 8px;
`;

const SupersetButton = styled('button')`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  height: 32px;
  padding: 0 8px 0 6px;
  background: #FFFFFF;
  border: 1px solid #EEEEEE;
  border-radius: 4px;
  font-family: 'Nunito Sans';
  font-size: 14px;
  color: #444444;
  cursor: pointer;
  &:focus { outline: none; }
  &:focus-visible { outline: none; box-shadow: 0 0 0 2px #BFDBFE; }
`;

const SupersetSwatch = styled('div')`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 22px;
  height: 22px;
  border-radius: 4px;
  background: ${(props) => props.background || '#EEEEEE'};
  color: #444444;
  font-size: 13px;
  font-weight: 600;
`;

const SupersetEmptyHint = styled('span')`
  display: inline-flex;
  align-items: center;
  height: 22px;
  padding: 0 2px;
  font-family: 'Nunito Sans';
  font-size: 13px;
  color: #9E9E9E;
`;

const SupersetPopover = styled('div')`
  position: fixed;
  z-index: 1100;
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  padding: 10px;
  width: 220px;
  background: white;
  border: 1px solid #EEEEEE;
  border-radius: 6px;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.12);
`;

const SupersetChip = styled('button')`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  padding: 0;
  border-radius: 4px;
  background: ${(props) => props.background || 'white'};
  border: 2px solid ${(props) => (props.isSelected ? '#77A7FF' : '#EEEEEE')};
  font-family: 'Nunito Sans';
  font-size: 13px;
  font-weight: 600;
  color: ${(props) => (props.muted ? '#9E9E9E' : '#444444')};
  cursor: pointer;
  &:hover { border-color: ${(props) => (props.isSelected ? '#77A7FF' : '#BDBDBD')}; }
  &:focus { outline: none; }
  &:focus-visible { outline: none; box-shadow: 0 0 0 2px #BFDBFE; }
`;

const StateBadge = styled('div')`
  display: inline-flex;
  align-items: center;
  flex-shrink: 0;
  height: 22px;
  padding: 0 8px;
  font-family: 'Nunito Sans';
  font-size: 12px;
  font-weight: 500;
  color: #6B6B6B;
  background: #F2F2F2;
  border-radius: 11px;
  white-space: nowrap;
`;

const SetsCountLabel = styled('div')`
  font-family: 'Nunito Sans';
  font-size: 14px;
  font-weight: 600;
  color: #444444;
`;

const SetsFieldGroup = styled('div')`
  position: relative;
  display: inline-flex;
  align-items: center;
  gap: 12px;
`;

const SetsSubtext = styled('div')`
  position: absolute;
  top: calc(100% + 4px);
  left: 0;
  white-space: nowrap;
  font-family: 'Nunito Sans';
  font-size: 12px;
  color: #9E9E9E;
`;

const SetsStepper = styled('div')`
  display: inline-flex;
  align-items: stretch;
  height: 32px;
  border: 1px solid #EEEEEE;
  border-radius: 4px;
  background: #FFFFFF;
  overflow: hidden;
`;

const StepperButton = styled('button')`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  padding: 0;
  border: none;
  background: transparent;
  color: #444444;
  font-family: 'Nunito Sans';
  font-size: 16px;
  line-height: 1;
  cursor: pointer;
  &:hover:not(:disabled) { background: #F0F0F0; }
  &:focus { outline: none; }
  &:focus-visible {
    outline: none;
    background: #E8F0FE;
    box-shadow: inset 0 0 0 2px #BFDBFE;
  }
  &:disabled {
    color: #D5D5D5;
    cursor: not-allowed;
  }
`;

const StepperInput = styled('input')`
  width: 35px;
  border: none;
  border-left: 1px solid #EEEEEE;
  border-right: 1px solid #EEEEEE;
  background: transparent;
  font-family: 'Nunito Sans';
  font-size: 14px;
  font-weight: 600;
  color: #444444;
  text-align: center;
  -moz-appearance: textfield;
  &::-webkit-outer-spin-button,
  &::-webkit-inner-spin-button {
    -webkit-appearance: none;
    margin: 0;
  }
  &:focus { outline: none; }
`;

const TransposedScroll = styled('div')`
  width: 100%;
  overflow-x: auto;
  padding-left: 6px;
`;

const TransposedTable = styled('div')`
  display: flex;
  flex-direction: column;
  width: 100%;
`;

const TransposedRow = styled('div')`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 6px 0;
  width: 100%;
`;

const PropertyLabelCell = styled('div')`
  display: flex;
  align-items: center;
  min-width: 120px;
  max-width: 120px;
  font-family: 'Nunito Sans';
  font-size: 14px;
  font-weight: 400;
  color: #444444;
`;

const PropertyInputCell = styled('div')`
  display: flex;
  align-items: center;
  flex: 1 1 80px;
  min-width: 80px;
  > div {
    min-width: 0;
    width: 100%;
  }
  input {
    min-width: 0;
    width: 100%;
    box-sizing: border-box;
  }
`;

const TransposedFooter = styled('div')`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 12px;
  padding-top: 8px;
  padding-bottom: 4px;
`;

const CompactSaveButton = styled('button')`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 6px 12px;
  background: #10cd8c;
  color: ${(props) => (props.shimmer ? 'transparent' : 'white')};
  border: 1px solid transparent;
  border-radius: 4px;
  font-family: 'Nunito Sans';
  font-weight: 700;
  font-size: 14px;
  line-height: 18px;
  cursor: pointer;
  &:hover { opacity: 0.92; }
  &:focus { outline: none; }
  &:focus-visible {
    outline: none;
    box-shadow: 0 0 0 2px #FFFFFF, 0 0 0 4px #BFDBFE;
  }
`;

const PreSaveTableTransposedCircuit = ({
  coachCompletion,
  completionOnly,
  contentIndex,
  content,
  localAdditionalInfo,
  localDescription,
  swapDropdownSelectedExercise,
  date,
  dayNumber,
  measure,
}) => {
  const dispatch = useDispatch();
  const tableContainerRef = useRef(null);

  const targetRef = useRef(null);
  const [showTooltip, setShowTooltip] = useState(false);
  const [isAdding, setIsAdding] = useState(false);
  // eslint-disable-next-line no-unused-vars
  const [errors, setErrors] = useState({});
  const errorTooltipId = `circuit-presave-error-tooltip-${content.assignedId}`;
  const programType = useSelector((state) => state.calendar.data.programType);
  const selectedProgramDayNumber = useSelector(
    (state) => state.calendar.data.selectedProgramDayNumber,
  );
  const currentUser = useSelector((state) => state.auth.data.currentUser);
  const selectedDate = useSelector((state) => state.calendar.data.selectedDate);
  const effectiveDate = date || selectedDate;
  const endpointFormattedDate = moment(effectiveDate).format('YYYY-MM-DD');
  const calendarContainerScrollPositionTop = useSelector((state) => state.calendar.data.calendarContainerScrollPositionTop);
  const focusTarget = useSelector((state) => state.calendar.data.focusTarget);
  const preSaveTableLocalStateForItem = useSelector(
    (state) => state.calendar.data.preSaveTableLocalState[content.assignedId],
  );
  const [setNumberValue, setSetNumberValue] = useState(content?.preSaveRounds || 1);
  /** Local grouping/superset state — hydrated from content, persisted via
   * the backup, and applied to the save payload. */
  const [groupingLetter, setGroupingLetter] = useState(content?.groupingLetter || null);
  const [groupingColorCode, setGroupingColorCode] = useState(content?.groupingColorCode || null);
  const [isSupersetOpen, setIsSupersetOpen] = useState(false);
  const [supersetPopoverPos, setSupersetPopoverPos] = useState(null);
  const supersetButtonRef = useRef(null);
  const supersetPopoverRef = useRef(null);
  const supersetChipRefs = useRef([]);
  const [supersetFocusIndex, setSupersetFocusIndex] = useState(0);
  const [exerciseData, setExerciseData] = useState({ reps: content?.detailType === 'reps' ? content?.detail : null, sets: content?.detailType === 'sets' ? content?.detail : null });
  const [time, setTime] = useState(!content?.detailType ? content?.detail : null);
  const [rest, setRest] = useState(content?.preSaveRest || null);

  /** This useEffect detects if the user chooses a different exercise
   * from the item dropdownn select and updates the pre saved item in redux
   * so that the table structure and content updates to match the selected exercise
   */
  useEffect(() => {
    if (swapDropdownSelectedExercise) {
      const updatedItem = {
        ...swapDropdownSelectedExercise,
        icon: 'circuit',
        title: swapDropdownSelectedExercise.name,
        exerciseId: parseInt(swapDropdownSelectedExercise.id, 10),
        id: `${swapDropdownSelectedExercise.id}-${generateUniqueId()}`,
        assignedId: `${swapDropdownSelectedExercise.id}-${generateUniqueId()}`,
        preSave: true,
      };
      if (programType === 'free-form-program') {
        dispatch(updateReduxProgramItem(updatedItem, dayNumber || selectedProgramDayNumber, contentIndex));
      } else {
        dispatch(updateReduxCalendarItem(updatedItem, endpointFormattedDate, contentIndex));
      }
    }
  }, [swapDropdownSelectedExercise]);

  const [primaryRows, setPrimaryRows] = useState([
    { name: 'rounds' },
    { name: 'rest', label: 'Rest (s)' },
    /** { name: 'time limit' }, */
    /** need to bring in initial values off of conent to determine if reps or sets are shown */
    { name: ((content?.inputType === 'T' || content?.inputType === 'C') && content.detailType === 'sets') || content.inputType === 'S' ? 'sets' : 'reps' },
    { name: 'add-property', label: 'Add Property' },
  ]);

  const [primaryRowSwitch, setPrimaryRowSwitch] = useState({
    rounds: true,
    rest: true,
    /** 'time limit': true, */
    /** need to bring in initial value off of conent */
    reps: ((content?.inputType === 'T' || content?.inputType === 'C') && content.detailType === 'reps') || content.inputType === 'R',
    /** need to bring in initial value off of conent */
    sets: ((content?.inputType === 'T' || content?.inputType === 'C') && content.detailType === 'sets') || content.inputType === 'S',
    'add-property': true,
  });

  const addPropertyInitialized = useRef(false);

  // Turn off 'add-property' when all optional properties are enabled
  useEffect(() => {
    // Skip on initial mount to allow backup restoration to complete
    if (!addPropertyInitialized.current) {
      addPropertyInitialized.current = true;
      return;
    }

    // For circuit tables, only rest and one of (reps OR sets) can be enabled at a time
    const allOptionalPropertiesEnabled = primaryRowSwitch.rest
    && (primaryRowSwitch.reps || primaryRowSwitch.sets);

    if (allOptionalPropertiesEnabled && primaryRowSwitch['add-property']) {
      setPrimaryRowSwitch((prev) => ({
        ...prev,
        'add-property': false,
      }));
    } else if (!allOptionalPropertiesEnabled && !primaryRowSwitch['add-property']) {
      // Re-enable add-property if any optional property is turned off
      setPrimaryRowSwitch((prev) => ({
        ...prev,
        'add-property': true,
      }));
    }
  }, [
    primaryRowSwitch.rest,
    primaryRowSwitch.reps,
    primaryRowSwitch.sets,
  ]);


  useEffect(() => {
    if (!primaryRowSwitch.rest) {
      setRest(null);
    }
  }, [primaryRowSwitch]);

  // Initialize state from Redux backup if it exists
  // Only runs on mount or when switching to a different item (not on every backup change)
  useEffect(() => {
    const backup = preSaveTableLocalStateForItem;
    if (backup) {
      if (backup.setNumberValue !== undefined) setSetNumberValue(backup.setNumberValue);
      if (backup.exerciseData !== undefined) setExerciseData(backup.exerciseData);
      if (backup.time !== undefined) setTime(backup.time);
      if (backup.rest !== undefined) setRest(backup.rest);
      if (backup.primaryRowSwitch !== undefined) setPrimaryRowSwitch(backup.primaryRowSwitch);
      if (backup.primaryRows !== undefined) setPrimaryRows(backup.primaryRows);
      if (backup.groupingLetter !== undefined) setGroupingLetter(backup.groupingLetter);
      if (backup.groupingColorCode !== undefined) setGroupingColorCode(backup.groupingColorCode);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [content.assignedId]);

  // Save current state to Redux backup on blur
  const saveStateToRedux = useCallback(() => {
    const stateObject = {
      setNumberValue,
      exerciseData,
      time,
      rest,
      primaryRowSwitch,
      primaryRows,
      groupingLetter,
      groupingColorCode,
    };
    dispatch(setPreSaveTableLocalState(content.assignedId, stateObject));
  }, [
    setNumberValue, exerciseData, time, rest, primaryRowSwitch, primaryRows,
    groupingLetter, groupingColorCode, content.assignedId, dispatch,
  ]);

  // Track if we've completed initial mount/restore cycle
  const hasInitialized = useRef(false);

  // Auto-save state to Redux on every change (not just blur)
  // This ensures state is preserved when component re-renders (e.g., superset selection)
  useEffect(() => {
    // Skip the initial render cycle - only save after user makes changes
    if (!hasInitialized.current) {
      hasInitialized.current = true;
      return;
    }
    saveStateToRedux();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    setNumberValue,
    exerciseData,
    time,
    rest,
    primaryRowSwitch,
    primaryRows,
  ]);

  const setRestFunction = (value) => {
    // Empty input clears rest rather than storing NaN (which would surface as
    // an invalid controlled input value and propagate into the payload).
    if (value === '' || value === null || value === undefined) {
      setRest(null);
      return;
    }
    const intRest = parseInt(value, 10);
    // Ignore non-numeric and negative values
    if (Number.isNaN(intRest) || intRest < 0) return;
    setRest(intRest);
  };

  const setTimeFunction = (value) => {
    setTime(value);
  };

  const handleInputChange = (field, value) => {
    // Prevent negative values for numeric fields
    if ((field === 'reps' || field === 'sets') && parseFloat(value) < 0) {
      return;
    }
    setExerciseData((prev) => ({ ...prev, [field]: value }));
  };

  const typeFormatter = (typeDescription) => {
    if (typeDescription === 'lift') {
      return 'lift';
    }
    if (typeDescription === 'cardio') {
      return 'saq+c';
    }
    if (typeDescription === 'circuit') {
      return 'circuit';
    }
    return '';
  };

  const errorAddReset = () => {
    setIsAdding(false);
  };

  const handleSaveCalendarExercise = (contentIndexParam) => {
    setIsAdding(true);
    const setSubmissionArray = [];
    for (let i = 0; i < setNumberValue; i += 1) {
      const setObject = {
        setNumber: i + 1,
      };
      if (time) {
        setObject.prescribedValue = time;
      }
      if (exerciseData.reps) {
        setObject.reps = exerciseData.reps;
      }
      setSubmissionArray.push(setObject);
    }
    const submissionObject = {
      coachCompletion,
      completionOnly,
      /** Conditional key based on programType: "date" OR "dayNumber" */
      [programType === 'free-form-program' ? 'dayNumber' : 'date']:
        programType === 'free-form-program'
          ? dayNumber
          : moment(effectiveDate).format('YYYY-MM-DD'),
      exerciseId: content.exerciseId,
      type: typeFormatter(content.typeDescription),
      sets: setSubmissionArray,
      showSessionBreakItems: false,
    };
    if (rest) {
      submissionObject.restTime = rest;
    }
    if (localDescription || content.description) {
      submissionObject.description = localDescription || content.description;
    }
    if (localAdditionalInfo || content.additionalInfo) {
      submissionObject.additionalInfo = localAdditionalInfo || content.additionalInfo;
    }
    if (groupingLetter) {
      submissionObject.groupingLetter = groupingLetter;
      submissionObject.groupingColorCode = groupingColorCode;
    }
    const formattedDate = moment(effectiveDate).format('YYYY-MM-DD');
    if (programType === 'free-form-program') {
      dispatch(saveNonDateBasedWorkoutExercise(
        currentUser,
        contentIndexParam,
        submissionObject,
        dayNumber,
        errorAddReset,
        {
          onSuccess: () => {
          // Trigger focus to next preSave item
            dispatch(focusNextPreSaveProgramItem(contentIndexParam, dayNumber));
          },
          exerciseName: content.title,
        },
        content.assignedId,
      ));
    } else {
      dispatch(saveCalendarExercise(
        currentUser,
        contentIndexParam,
        submissionObject,
        formattedDate,
        errorAddReset,
        {
          onSuccess: () => {
          // Trigger focus to next preSave item
            dispatch(focusNextPreSaveItem(contentIndexParam, effectiveDate));
          },
          exerciseName: content.title,
        },
        content.assignedId,
      ));
    }
  };

  const rowValueDecider = (name, shouldShimmer) => {
    if (shouldShimmer) {
      return (
        <Shimmer>
          <RowValueContainer shimmer>
            <ShimmerRowValue />
          </RowValueContainer>
        </Shimmer>
      );
    }
    if (name === 'rest') {
      return (
        <RowValueContainer>
          <InputWithErrorWrapper>
            <RowValue
              isError={!!errors.rest}
              type='number'
              value={rest}
              onChange={(e) => setRestFunction(e.target.value)}
              onBlur={saveStateToRedux}
              onKeyDown={(e) => {
                if (e.key === '-') {
                  e.preventDefault();
                }
              }}
              background='#FCFCFC'
              data-focus-target={
                (name === 'rest' && primaryRowSwitch.rest && !primaryRowSwitch.rounds)
                  ? content.assignedId
                  : undefined
              }
            />
            {errors.rest && (
              <InputError
                data-tooltip-id={errorTooltipId}
                data-tooltip-content={errors.rest}
              >
                {errors.rest}
              </InputError>
            )}
          </InputWithErrorWrapper>
        </RowValueContainer>
      );
    }
    if (name === 'completed time') {
      return (
        <RowValueContainer>
          <RowValue
            value={time}
            placeholder='-'
            /** disable the ability to edit this field */
            disabled
            onChange={(e) => setTimeFunction(e.target.value)}
            background='#FCFCFC'
            textColor='#BDBDBD'
          />
        </RowValueContainer>
      );
    }
    return (
      <RowValueContainer>
        <RowValue
          value={name === 'reps' ? exerciseData?.reps : exerciseData?.sets}
          /** disable ablity to edit these fields */
          disabled
          placeholder='-'
          type='text'
          background='#FCFCFC'
          textColor='#BDBDBD'
          onChange={(e) => handleInputChange(name, e.target.value)}
          onKeyDown={(e) => {
            // Prevent minus key for numeric fields
            if ((name === 'reps' || name === 'sets') && e.key === '-') {
              e.preventDefault();
            }
          }}
        />
      </RowValueContainer>
    );
  };

  const updateColumnName = useCallback((oldName, newName) => {
    // Prevent updating the add-property column
    if (oldName === 'add-property' || newName === 'add-property') return;

    setPrimaryRows(
      (prevRows2) => prevRows2.map(
        (row) => (row.name === oldName ? { ...row, name: newName } : row),
      ),
    );

    setPrimaryRowSwitch((prevSwitch) => ({
      ...prevSwitch,
      [oldName]: false,
      [newName]: true,
    }));
  }, []);

  const findCorrectUnit = (unitContent) => {
    if (unitContent?.unitAbbr) {
      return `(${unitContent?.unitAbbr === 'wgt' ? 'lbs' : unitContent?.unitAbbr})`;
    }
    return '';
  };

  // This useEffect is responsible for managing focus on input fields when the focusTarget changes.
  // It checks if the current focusTarget's assignedId matches the content's assignedId.
  // If they match, it finds the first input element with a data-focus-target attribute equal to the content's assignedId.
  // Once found, it sets a timeout to focus on this input element after a short delay, ensuring the DOM is ready.
  useEffect(() => {
    if (focusTarget?.assignedId === content.assignedId) {
      const firstInput = document.querySelector(`[data-focus-target="${content.assignedId}"]`);
      if (firstInput) {
        setTimeout(() => {
          firstInput.focus();
        }, 100); // Small delay to ensure DOM is ready
      }
    }
  }, [focusTarget, content.assignedId]);

  /** Re-measure the virtualized list cell when the card's vertical height
   * changes — adding/removing a property (primaryRowSwitch), error messages,
   * or the status pills. Skip the first run; the CellMeasurer wrapper handles
   * the initial measure. */
  const hasMeasureMounted = useRef(false);
  useEffect(() => {
    if (!hasMeasureMounted.current) {
      hasMeasureMounted.current = true;
      return;
    }
    if (measure) measure();
  }, [primaryRowSwitch, errors, coachCompletion, completionOnly]);

  /** Property label content for the transposed label cell. */
  const renderPropertyLabel = (primaryRow) => {
    const isAthleteInput = (primaryRow.name === 'reps' && content.inputType === 'R')
      || (primaryRow.name === 'sets' && content.inputType === 'S')
      || (primaryRow.name === 'completed time' && content.inputType === 'T');
    if (isAthleteInput) {
      return (
        <NameAndDropdown ref={targetRef}>
          <InfoIconWrapper>
            <IcomoonReact
              iconSet={iconSet}
              size={14}
              icon='info2'
              color='rgba(189, 189, 189, 1)'
              style={{ cursor: 'pointer' }}
              onMouseEnter={() => setShowTooltip(true)}
              onMouseLeave={() => setShowTooltip(false)}
            />
          </InfoIconWrapper>
          {showTooltip && (
            <TBportalTooltip targetRef={targetRef} backgroundColor='rgba(68, 68, 68, 1)' setShowToolTip={setShowTooltip} scrollContainerRefTopPosition={calendarContainerScrollPositionTop}>
              <TooltipDataContainer>
                <Text fontSize='13px' fontWeight={400} color='white'>Athlete input only</Text>
              </TooltipDataContainer>
            </TBportalTooltip>
          )}
          <SwapPropDropdownCircuit
            visible={false}
            setPrimaryRowSwitch={setPrimaryRowSwitch}
            primaryRowSwitch={primaryRowSwitch}
            rowName={primaryRow.name}
            size={12}
            updateColumnName={updateColumnName}
            name={primaryRow.name === 'time' ? capitalizeWords('Prescribed Value') : capitalizeWords(primaryRow.label || primaryRow.name)}
            unit={primaryRow.name === 'time' ? findCorrectUnit(content) : null}
          />
        </NameAndDropdown>
      );
    }
    return (
      <NameAndDropdown>
        <SwapPropDropdownCircuit
          visible={primaryRow.name === 'rounds' || primaryRow.name === 'rest'}
          setPrimaryRowSwitch={setPrimaryRowSwitch}
          primaryRowSwitch={primaryRowSwitch}
          rowName={primaryRow.name}
          size={12}
          updateColumnName={updateColumnName}
          name={primaryRow.name === 'time' ? capitalizeWords('Prescribed Value') : capitalizeWords(primaryRow.label || primaryRow.name)}
          unit={primaryRow.name === 'time' ? findCorrectUnit(content) : null}
        />
      </NameAndDropdown>
    );
  };

  /** SUPERSET picker — A–Z plus a clear option. */
  const SUPERSET_OPTIONS = [
    { letter: null, color: null },
    ...Object.entries(SUPERSET_DEFAULTS).map(([letter, color]) => ({ letter, color })),
  ];

  const chooseSuperset = useCallback((letter, color) => {
    setGroupingLetter(letter);
    setGroupingColorCode(color);
    setIsSupersetOpen(false);
    /** Apply to the pre-save item in redux immediately so the item icon
     * shows the grouping right away (parity with the other superset
     * pickers) instead of waiting for save. */
    const updatedItem = { ...content, groupingLetter: letter, groupingColorCode: color };
    if (programType === 'free-form-program') {
      dispatch(
        updateReduxProgramItem(updatedItem, dayNumber || selectedProgramDayNumber, contentIndex),
      );
    } else {
      dispatch(updateReduxCalendarItem(updatedItem, endpointFormattedDate, contentIndex));
    }
    setTimeout(() => saveStateToRedux(), 0);
    setTimeout(() => supersetButtonRef.current?.focus(), 0);
  }, [
    saveStateToRedux, content, programType, dayNumber,
    selectedProgramDayNumber, contentIndex, endpointFormattedDate, dispatch,
  ]);

  const selectSupersetByKey = useCallback((key) => {
    if (key === 'Backspace' || key === 'Delete' || key === '0') {
      chooseSuperset(null, null);
      return true;
    }
    if (/^[a-zA-Z]$/.test(key)) {
      const letter = key.toUpperCase();
      const color = SUPERSET_DEFAULTS[letter];
      if (color) {
        chooseSuperset(letter, color);
        return true;
      }
    }
    return false;
  }, [chooseSuperset]);

  useEffect(() => {
    if (!isSupersetOpen) return undefined;
    const onMouseDown = (e) => {
      if (supersetPopoverRef.current?.contains(e.target)) return;
      if (supersetButtonRef.current?.contains(e.target)) return;
      setIsSupersetOpen(false);
    };
    const onKey = (e) => {
      if (e.key === 'Escape') {
        setIsSupersetOpen(false);
        supersetButtonRef.current?.focus();
      }
    };
    document.addEventListener('mousedown', onMouseDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onMouseDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [isSupersetOpen]);

  const computeSupersetPos = useCallback(() => {
    const btn = supersetButtonRef.current;
    if (!btn) return null;
    const rect = btn.getBoundingClientRect();
    const POPOVER_WIDTH = 220;
    const VIEWPORT_PAD = 8;
    let left = rect.left;
    if (left + POPOVER_WIDTH > window.innerWidth - VIEWPORT_PAD) {
      left = Math.max(VIEWPORT_PAD, window.innerWidth - POPOVER_WIDTH - VIEWPORT_PAD);
    }
    return { top: rect.bottom + 4, left };
  }, []);

  useLayoutEffect(() => {
    if (!isSupersetOpen) return undefined;
    setSupersetPopoverPos(computeSupersetPos());
    const onScrollOrResize = () => {
      const next = computeSupersetPos();
      if (!next) return;
      setSupersetPopoverPos((prev) => {
        if (prev && prev.top === next.top && prev.left === next.left) return prev;
        return next;
      });
    };
    window.addEventListener('scroll', onScrollOrResize, true);
    window.addEventListener('resize', onScrollOrResize);
    return () => {
      window.removeEventListener('scroll', onScrollOrResize, true);
      window.removeEventListener('resize', onScrollOrResize);
    };
  }, [isSupersetOpen, computeSupersetPos]);

  useEffect(() => {
    if (!isSupersetOpen) return;
    const idx = groupingLetter
      ? SUPERSET_OPTIONS.findIndex((o) => o.letter === groupingLetter)
      : 0;
    setSupersetFocusIndex(idx >= 0 ? idx : 0);
    setTimeout(() => supersetChipRefs.current[idx >= 0 ? idx : 0]?.focus(), 0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSupersetOpen]);

  const moveSupersetFocus = (delta) => {
    const next = (supersetFocusIndex + delta + SUPERSET_OPTIONS.length) % SUPERSET_OPTIONS.length;
    setSupersetFocusIndex(next);
    supersetChipRefs.current[next]?.focus();
  };

  return (
    <FormWrapper
      ref={tableContainerRef}
      onSubmit={(e) => {
        e.preventDefault();
        handleSaveCalendarExercise(contentIndex);
      }}
    >
      <SetsCountHeader>
        <SetsFieldGroup>
          <SetsCountLabel>Rounds</SetsCountLabel>
          <SetsStepper>
            <StepperButton
              type='button'
              aria-label='Decrease rounds'
              onClick={() => {
                const next = Math.max(1, parseInt(setNumberValue, 10) - 1);
                setSetNumberValue(next);
                saveStateToRedux();
              }}
              disabled={parseInt(setNumberValue, 10) <= 1}
            >
              −
            </StepperButton>
            <StepperInput
              type='text'
              inputMode='numeric'
              value={setNumberValue}
              data-focus-target={primaryRowSwitch.rounds ? content.assignedId : undefined}
              onFocus={(e) => e.target.select()}
              onChange={(e) => {
                const raw = e.target.value.replace(/[^0-9]/g, '');
                if (raw === '') { setSetNumberValue(''); return; }
                let value = parseInt(raw, 10);
                if (value > 10) value = parseInt(raw.slice(-1), 10);
                setSetNumberValue(Math.min(10, Math.max(1, value)));
              }}
              onBlur={(e) => {
                const value = parseInt(e.target.value, 10);
                if (Number.isNaN(value) || value < 1) setSetNumberValue(1);
                saveStateToRedux();
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter') { e.preventDefault(); e.target.blur(); }
              }}
              aria-label='Number of rounds'
            />
            <StepperButton
              type='button'
              aria-label='Increase rounds'
              onClick={() => {
                const next = Math.min(10, parseInt(setNumberValue, 10) + 1);
                setSetNumberValue(next);
                saveStateToRedux();
              }}
              disabled={parseInt(setNumberValue, 10) >= 10}
            >
              +
            </StepperButton>
          </SetsStepper>
          <SetsSubtext># of Rounds: 1-10</SetsSubtext>
        </SetsFieldGroup>

        <SetsCountLabel>Superset</SetsCountLabel>
        <SupersetWrapper>
          <SupersetButton
            ref={supersetButtonRef}
            type='button'
            aria-haspopup='listbox'
            aria-expanded={isSupersetOpen}
            aria-label={groupingLetter ? `Superset ${groupingLetter}, change` : 'Select superset'}
            aria-keyshortcuts='A-Z'
            title='Type a letter (A–Z) to set a superset, or Backspace to clear'
            onClick={() => setIsSupersetOpen((v) => !v)}
            onKeyDown={(e) => {
              if (e.key === 'ArrowDown' || e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                setIsSupersetOpen(true);
                return;
              }
              if (selectSupersetByKey(e.key)) { e.preventDefault(); }
            }}
          >
            {groupingLetter ? (
              <SupersetSwatch background={hexToRgba(groupingColorCode || SUPERSET_DEFAULTS[groupingLetter], 0.2)}>
                {groupingLetter}
              </SupersetSwatch>
            ) : (
              <SupersetEmptyHint>None</SupersetEmptyHint>
            )}
            <IcomoonReact iconSet={iconSet} size={9} icon='down-arrow' color='#9E9E9E' />
          </SupersetButton>
          {isSupersetOpen && createPortal(
            <SupersetPopover
              ref={supersetPopoverRef}
              className='transposed-superset-popover'
              role='listbox'
              aria-label='Superset grouping'
              style={{
                top: supersetPopoverPos?.top ?? -9999,
                left: supersetPopoverPos?.left ?? -9999,
                visibility: supersetPopoverPos ? 'visible' : 'hidden',
              }}
              onKeyDown={(e) => {
                if (e.key === 'ArrowRight') { e.preventDefault(); moveSupersetFocus(1); }
                else if (e.key === 'ArrowLeft') { e.preventDefault(); moveSupersetFocus(-1); }
                else if (e.key === 'ArrowDown') { e.preventDefault(); moveSupersetFocus(6); }
                else if (e.key === 'ArrowUp') { e.preventDefault(); moveSupersetFocus(-6); }
                else if (selectSupersetByKey(e.key)) { e.preventDefault(); }
              }}
            >
              {SUPERSET_OPTIONS.map((opt, idx) => {
                const isSelected = groupingLetter === opt.letter;
                const bg = opt.color ? hexToRgba(opt.color, 0.2) : 'white';
                return (
                  <SupersetChip
                    key={opt.letter || 'none'}
                    type='button'
                    role='option'
                    aria-selected={isSelected}
                    isSelected={isSelected}
                    background={bg}
                    muted={!opt.letter}
                    tabIndex={supersetFocusIndex === idx ? 0 : -1}
                    ref={(el) => { supersetChipRefs.current[idx] = el; }}
                    onClick={() => chooseSuperset(opt.letter, opt.color)}
                    onFocus={() => setSupersetFocusIndex(idx)}
                  >
                    {opt.letter || '—'}
                  </SupersetChip>
                );
              })}
            </SupersetPopover>,
            document.body,
          )}
        </SupersetWrapper>

        <StatusBadgeScroller>
          {coachCompletion && <StateBadge>Coach Completion</StateBadge>}
          {completionOnly && <StateBadge>Completion Only</StateBadge>}
        </StatusBadgeScroller>
      </SetsCountHeader>

      <TransposedScroll>
        <TransposedTable>
          {primaryRows.map((primaryRow) => {
            if (primaryRow.name === 'rounds' || primaryRow.name === 'add-property') return null;
            if (!primaryRowSwitch[primaryRow.name]) return null;
            return (
              <TransposedRow key={`prop-${primaryRow.name}`}>
                <PropertyLabelCell>
                  {renderPropertyLabel(primaryRow)}
                </PropertyLabelCell>
                <PropertyInputCell>
                  {rowValueDecider(primaryRow.name, isAdding)}
                </PropertyInputCell>
              </TransposedRow>
            );
          })}
        </TransposedTable>
      </TransposedScroll>

      <TransposedFooter>
        {!isAdding ? (
          <CompactSaveButton type='submit'>
            SAVE
            <IcomoonReact iconSet={iconSet} size={10} icon='down-arrow' color='white' />
          </CompactSaveButton>
        ) : (
          <Shimmer>
            <CompactSaveButton shimmer>
              SAVE
              <IcomoonReact iconSet={iconSet} size={10} icon='down-arrow' color='transparent' />
            </CompactSaveButton>
          </Shimmer>
        )}
      </TransposedFooter>
      <ReactTooltip
        id={errorTooltipId}
        place='top'
        positionStrategy='fixed'
        delayShow={300}
        style={{
          zIndex: 9999,
          backgroundColor: 'rgba(68, 68, 68, 1)',
          color: 'white',
          fontFamily: 'Nunito Sans',
          fontSize: '12px',
          maxWidth: '260px',
          padding: '6px 10px',
          lineHeight: 1.3,
        }}
      />
    </FormWrapper>
  );
};

PreSaveTableTransposedCircuit.propTypes = {
  coachCompletion: PropTypes.bool.isRequired,
  completionOnly: PropTypes.bool.isRequired,
  contentIndex: PropTypes.number.isRequired,
  content: PropTypes.instanceOf(Object).isRequired,
  localAdditionalInfo: PropTypes.string,
  localDescription: PropTypes.string,
  swapDropdownSelectedExercise: PropTypes.instanceOf(Object).isRequired,
  date: PropTypes.string,
  dayNumber: PropTypes.string,
  measure: PropTypes.func,
};
PreSaveTableTransposedCircuit.defaultProps = {
  localAdditionalInfo: '',
  localDescription: '',
  date: null,
  dayNumber: null,
  measure: null,
};

export default PreSaveTableTransposedCircuit;
